const TRANSLATION_CACHE_KEY = 'rio-translation-cache-v1'
const TRANSLATION_TIMEOUT_MS = 7000
const TRANSLATION_CACHE_LIMIT = 1000
const TRANSLATION_WARN_LIMIT = 20
const WOWHEAD_WORKER_URL = 'https://wowhead-rss-proxy.andrey-ilutsa.workers.dev'
const WORKER_METADATA_PENDING_PROVIDER = {
  key: 'worker-metadata-pending',
  label: 'Update worker',
  status: 'pending',
}
const NO_TRANSLATE_PROVIDER = { key: 'none', label: 'No translate', status: 'none' }
const LIMIT_PROVIDER = { key: 'limit', label: 'Limit reached', status: 'limit' }

const warnedProviders = new Set()
let lastTranslationProvider = NO_TRANSLATE_PROVIDER

function warnProvider(provider, error) {
  const key = `${provider}:${error?.message || error}`
  if (warnedProviders.has(key) || warnedProviders.size >= TRANSLATION_WARN_LIMIT) return
  warnedProviders.add(key)
  console.warn(`Translation provider failed: ${provider}`, error)
}

function emitTranslationProvider(provider) {
  if (!provider?.label) return
  const previousKey = `${lastTranslationProvider?.key || ''}:${lastTranslationProvider?.label || ''}:${lastTranslationProvider?.cached || ''}:${lastTranslationProvider?.index || ''}:${lastTranslationProvider?.status || ''}`
  const nextKey = `${provider.key || ''}:${provider.label || ''}:${provider.cached || ''}:${provider.index || ''}:${provider.status || ''}`
  if (previousKey === nextKey) return
  lastTranslationProvider = provider
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('rio:translation-provider', {
      detail: provider,
    }))
  }
}

function isSupportedTarget(targetLang) {
  return ['en', 'uk', 'ru'].includes(targetLang)
}

function hashText(value = '') {
  let hash = 2166136261
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0).toString(36)
}

function cacheKey(text, targetLang) {
  return `${targetLang}:${hashText(text)}`
}

function readCache() {
  try {
    const cache = JSON.parse(localStorage.getItem(TRANSLATION_CACHE_KEY) || '{}')
    return cache && typeof cache === 'object' ? cache : {}
  } catch {
    return {}
  }
}

function writeCache(cache) {
  try {
    const entries = Object.entries(cache)
    const limited = entries.length > TRANSLATION_CACHE_LIMIT
      ? Object.fromEntries(entries.slice(entries.length - TRANSLATION_CACHE_LIMIT))
      : cache
    localStorage.setItem(TRANSLATION_CACHE_KEY, JSON.stringify(limited))
  } catch {
    // Translation cache is best-effort only.
  }
}

function getCachedTranslation(text, targetLang) {
  const cache = readCache()
  const entry = cache[cacheKey(text, targetLang)]
  if (typeof entry === 'string') {
    return entry.trim()
      ? {
        text: entry,
        provider: {
          key: 'unknown-cache',
          label: 'Unknown provider',
          cached: true,
          status: 'cached',
        },
      }
      : null
  }

  if (entry && typeof entry === 'object' && typeof entry.text === 'string' && entry.text.trim()) {
    return {
      text: entry.text,
      provider: {
        key: entry.provider || 'local-cache',
        label: entry.providerLabel || 'Local cache',
        index: entry.providerIndex || null,
        total: entry.providerCount || null,
        cached: true,
        status: entry.providerStatus || 'cached',
      },
    }
  }

  return null
}

function setCachedTranslation(text, targetLang, translatedText, provider = {}) {
  if (!text || !translatedText || translatedText === text) return
  const cache = readCache()
  cache[cacheKey(text, targetLang)] = {
    text: translatedText,
    provider: provider.key || '',
    providerLabel: provider.label || '',
    providerIndex: provider.index || null,
    providerCount: provider.total || null,
    providerStatus: provider.status || '',
  }
  writeCache(cache)
}

async function requestWithTimeout(url, options = {}) {
  const controller = new AbortController()
  const timeoutId = window.setTimeout(() => controller.abort(), TRANSLATION_TIMEOUT_MS)

  try {
    return await fetch(url, {
      ...options,
      signal: controller.signal,
    })
  } finally {
    window.clearTimeout(timeoutId)
  }
}

async function requestWorkerTranslation(text, targetLang) {
  const response = await requestWithTimeout(`${WOWHEAD_WORKER_URL}/translate`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      text,
      targetLang,
    }),
  })

  if (!response.ok) {
    throw new Error(`Translation worker failed: ${response.status}`)
  }

  const data = await response.json()
  const translated = String(data.translatedText || '').trim()
  const hasProviderMetadata = Boolean(data.providerLabel || data.providerIndex || data.providerStatus)
  return {
    text: translated || text,
    provider: hasProviderMetadata ? {
      key: data.provider || 'translation-worker',
      label: data.providerLabel || 'Translation worker',
      index: data.providerIndex || null,
      total: data.providerCount || null,
      cached: Boolean(data.cached),
      status: data.providerStatus || (data.provider === 'limit' ? 'limit' : 'translated'),
    } : WORKER_METADATA_PENDING_PROVIDER,
  }
}

export async function translateText(text, targetLang) {
  const sourceText = String(text || '').trim()
  if (!sourceText) return ''
  if (targetLang === 'en') {
    emitTranslationProvider(NO_TRANSLATE_PROVIDER)
    return sourceText
  }
  if (!isSupportedTarget(targetLang)) return sourceText

  try {
    const cached = getCachedTranslation(sourceText, targetLang)
    if (cached) {
      emitTranslationProvider(cached.provider)
      return cached.text
    }

    const result = await requestWorkerTranslation(sourceText, targetLang)
    emitTranslationProvider(result.provider)
    if (result.text && result.text !== sourceText) {
      setCachedTranslation(sourceText, targetLang, result.text, result.provider)
    }
    return result.text || sourceText
  } catch (error) {
    warnProvider('translateText', error)
    emitTranslationProvider(LIMIT_PROVIDER)
    return sourceText
  }
}

export function getLastTranslationProvider() {
  return lastTranslationProvider
}

export function setTranslationProvider(provider) {
  emitTranslationProvider(provider || NO_TRANSLATE_PROVIDER)
}

export async function mapWithConcurrency(items, limit, asyncMapper) {
  const results = new Array(items.length)
  let nextIndex = 0
  const workerCount = Math.min(Math.max(limit, 1), items.length)

  await Promise.all(Array.from({ length: workerCount }, async () => {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex
      nextIndex += 1
      results[currentIndex] = await asyncMapper(items[currentIndex], currentIndex)
    }
  }))

  return results
}

export async function translatePost(post, targetLang) {
  if (targetLang === 'en') return post

  try {
    const title = await translateText(post.title, targetLang)
    const summary = await translateText(post.summary, targetLang)

    return {
      ...post,
      title,
      summary,
      originalTitle: post.title,
      originalSummary: post.summary,
    }
  } catch (error) {
    warnProvider('translatePost', error)
    return post
  }
}

export async function translatePosts(posts, targetLang) {
  if (targetLang === 'en') return posts
  if (!Array.isArray(posts) || !posts.length) return posts

  try {
    return await mapWithConcurrency(posts, 3, (post) => translatePost(post, targetLang))
  } catch (error) {
    warnProvider('translatePosts', error)
    return posts
  }
}
